import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Ellipsis } from 'lucide-react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { strings } from '../strings';
import {
  NativeName,
  SourceChip,
  StatusBadgeView,
  StatusCellView,
  ValueCellView,
  VariableNameView,
} from './cells';
import { isGridKey, moveInGrid, type GridPosition } from './grid';
import {
  issueCounts,
  variableCellName,
  type RowStatus,
  type ValueCell,
  type VariableApp,
  type VariableRow,
} from './model';
import { locationEntry } from './registry';
import { changedText, type ColumnId, type Line, type ViewSettings } from './view';

const s = strings.variables;

export interface RowCallbacks {
  /** The cell's inline action (Set value, …) for the value column. */
  valueAction(row: VariableRow): ReactNode;
  /** The Actions column's primary action. */
  primaryAction(row: VariableRow): ReactNode;
  /** The row menu's entries. */
  menu(row: VariableRow): MenuEntry[];
  /** Enter / F2 on a cell. Returns true when it did something. */
  activate(row: VariableRow, column: ColumnId, key: 'Enter' | 'F2'): boolean;
  /** Delete on a row (design scope). */
  remove(row: VariableRow): void;
  /** Ctrl+C on a cell: the text to copy, or null. */
  copyText(row: VariableRow, column: ColumnId): string | null;
  /** Ctrl+C on a secret value that can be revealed (spec 6.10.18): Copy value. True when it started. */
  copySecret?(row: VariableRow): boolean;
  /** Ctrl+Enter on the value cell: expand a revealed value. True when it did. */
  expand?(row: VariableRow): boolean;
  details(row: VariableRow): void;
}

/** What a cell shows instead of its content while editing (inline name or value editors). */
export type CellOverride = (row: VariableRow, column: ColumnId) => ReactNode | undefined;

function headerLabel(column: ColumnId): string {
  return s.columns[column];
}

const SORTABLE: ReadonlySet<ColumnId> = new Set([
  'variable',
  'source',
  'nameInSource',
  'value',
  'status',
  'resource',
  'lastChanged',
  'version',
  'usedBy',
  'delivery',
]);

/**
 * The Variables view's table (spec 6.10.5, 6.10.16): a grid (flat) or treegrid (grouped) with one roving
 * cell, a two-line list below 760 px window width, or cards on phones.
 */
export function VariablesTable({
  lines,
  columns,
  settings,
  statuses,
  cells,
  selected,
  layout,
  label,
  typeLabel,
  callbacks,
  override,
  inserted,
  focusRequest,
  onFocusHandled,
  onActiveRow,
  groupAction,
  groupMenu,
  groupStatus,
  onAppDetails,
  onSort,
  onMoveColumn,
  onToggleGroup,
  onSelect,
  onSelectAll,
  onClearSelection,
}: {
  lines: readonly Line[];
  columns: readonly ColumnId[];
  settings: ViewSettings;
  statuses: ReadonlyMap<string, readonly RowStatus[]>;
  cells: ReadonlyMap<string, ValueCell>;
  selected: ReadonlySet<string>;
  layout: 'table' | 'list' | 'cards';
  label: string;
  /** The display name of a resource type, for group rows. */
  typeLabel?: (type: string) => string;
  callbacks: RowCallbacks;
  override?: CellOverride;
  /** A full-width line after a row (the change-source form), or at the top (`''`: add). */
  inserted?: { after: string; node: ReactNode } | null;
  /** Focus and scroll to this row when it changes. */
  focusRequest?: { rowId: string; nonce: number } | null;
  /** The focus request was handled: the owner clears it, so a remounted table doesn't handle it again. */
  onFocusHandled?: () => void;
  /** Focus moved to a variable row (the details pane follows it, spec 6.10.8). */
  onActiveRow?: (rowId: string) => void;
  /** An action on an app's group row (Refresh references). */
  groupAction?: (app: VariableApp) => ReactNode;
  /** The group row's menu (Shift+F10): Details of <app>, the rollout (feature spec 15 19.3). */
  groupMenu?: (app: VariableApp) => MenuEntry[];
  /** The app status words for the group row's accessible name. */
  groupStatus?: (app: VariableApp) => string | null;
  /** Alt+Enter on an app's group row. */
  onAppDetails?: (app: VariableApp) => void;
  onSort: (column: ColumnId) => void;
  onMoveColumn: (column: ColumnId, delta: -1 | 1) => void;
  onToggleGroup: (key: string) => void;
  onSelect: (rowId: string, mode: 'toggle' | 'range') => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
}) {
  const grouped = lines.some((l) => l.kind === 'group');
  const [focus, setFocus] = useState<GridPosition>({ row: 0, col: 0 });
  const [wanted, setWanted] = useState(0);
  const [moved, setMoved] = useState(0);
  const body = useRef<HTMLTableSectionElement | HTMLUListElement | null>(null);
  const rowCount = lines.length;
  const cellsIn = (index: number) =>
    lines[index]?.kind === 'group' || layout !== 'table' ? 1 : columns.length;
  const current = { row: Math.min(focus.row, Math.max(0, rowCount - 1)), col: focus.col };
  if (current.col >= cellsIn(current.row)) current.col = cellsIn(current.row) - 1;

  // Move DOM focus after keyboard moves and focus requests (not on every render: the user may be elsewhere).
  // Synchronously for keys; a request can come while the window is being mounted into its dock, so it waits a
  // frame until the window is in the document.
  useLayoutEffect(() => {
    if (moved === 0) return;
    const target = () =>
      body.current?.querySelector<HTMLElement>(`[data-cell="${current.row}:${current.col}"]`);
    const now = target();
    if (now?.isConnected && document.contains(now)) {
      now.focus({ preventScroll: false });
      return;
    }
    const frame = requestAnimationFrame(() => target()?.focus({ preventScroll: false }));
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moved]);

  // Requests to show a row (?var=, diagnostics, a new variable): focus moves there while rendering, the
  // scrolling happens after it.
  const [handled, setHandled] = useState<number | null>(null);
  if (!focusRequest && handled !== null) setHandled(null);
  if (focusRequest && focusRequest.nonce !== handled) {
    const index = lines.findIndex((l) => l.kind === 'row' && l.row.id === focusRequest.rowId);
    if (index >= 0) {
      setHandled(focusRequest.nonce);
      setFocus({ row: index, col: Math.max(0, Math.min(columns.indexOf('variable'), cellsIn(index) - 1)) });
      setMoved((n) => n + 1);
    }
  }
  useEffect(() => {
    if (!focusRequest || handled !== focusRequest.nonce) return;
    body.current?.querySelector(`[data-row-id="${CSS.escape(focusRequest.rowId)}"]`)?.scrollIntoView?.({
      block: 'nearest',
    });
    onFocusHandled?.();
  }, [focusRequest, handled, onFocusHandled]);

  const lineAt = (index: number) => lines[index];

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement;
    const cell = target.closest<HTMLElement>('[data-cell]');
    if (!cell) return;
    // Keys inside inline editors and controls belong to them.
    if (target !== cell && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    const [r, c] = cell.dataset.cell!.split(':').map(Number);
    const line = lineAt(r);
    const column = layout !== 'table' ? 'variable' : columns[c];
    const ctrl = event.ctrlKey || event.metaKey;
    const position = { row: r, col: c };
    // The canvas deletes the selected nodes on Delete/Backspace and other page shortcuts listen on the
    // document: keys pressed in the grid stay in the grid (the dock's tool-window shortcuts use Ctrl+Shift).
    if (isGridKey(event)) event.stopPropagation();
    if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) return;
    const next = moveInGrid(
      position,
      event,
      { rows: rowCount, cellsIn },
      line?.kind === 'group' ? wanted : c,
    );
    if (next && !event.altKey && !event.shiftKey) {
      event.preventDefault();
      setFocus(next.position);
      setWanted(next.wanted);
      setMoved((n) => n + 1);
      return;
    }
    if (line?.kind === 'group') {
      if (event.key === 'Enter' && event.altKey && line.app && onAppDetails) {
        event.preventDefault();
        onAppDetails(line.app);
        return;
      }
      if (((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu') && line.app) {
        event.preventDefault();
        document.querySelector<HTMLButtonElement>(`[data-group-menu="${CSS.escape(line.key)}"]`)?.click();
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleGroup(line.key);
      }
      return;
    }
    if (!line) return;
    const row = line.row;
    if (event.key === ' ' && !ctrl) {
      event.preventDefault();
      onSelect(row.id, event.shiftKey ? 'range' : 'toggle');
    } else if (event.key.toLowerCase() === 'a' && ctrl) {
      event.preventDefault();
      onSelectAll();
    } else if (event.key === 'Escape') {
      if (selected.size > 0) {
        event.preventDefault();
        event.stopPropagation();
        onClearSelection();
      }
    } else if (event.key === 'Enter' && event.altKey) {
      event.preventDefault();
      callbacks.details(row);
    } else if (event.key === 'Enter' && ctrl && (column === 'value' || layout !== 'table')) {
      if (callbacks.expand?.(row)) event.preventDefault();
    } else if (event.key === 'Enter' || event.key === 'F2') {
      if (callbacks.activate(row, column, event.key)) event.preventDefault();
    } else if (event.key === 'Delete') {
      event.preventDefault();
      callbacks.remove(row);
    } else if (event.key.toLowerCase() === 'c' && ctrl) {
      const text = callbacks.copyText(row, column);
      if (text !== null) {
        event.preventDefault();
        void navigator.clipboard?.writeText(text).catch(() => {});
      } else if (column === 'value' && callbacks.copySecret?.(row)) {
        event.preventDefault();
      }
    } else if ((event.key === 'F10' && event.shiftKey) || event.key === 'ContextMenu') {
      event.preventDefault();
      document.querySelector<HTMLButtonElement>(`[data-row-menu="${CSS.escape(row.id)}"]`)?.click();
    }
  };

  const cellProps = (row: number, col: number) => ({
    'data-cell': `${row}:${col}`,
    tabIndex: current.row === row && current.col === col ? 0 : -1,
    onFocus: () => {
      if (current.row !== row || current.col !== col) setFocus({ row, col });
      const line = lines[row];
      if (line?.kind === 'row') onActiveRow?.(line.row.id);
    },
  });

  const rowMenu = (row: VariableRow) => (
    <MenuButton
      portal
      placement="below-end"
      buttonClassName="btn btn-ghost btn-icon var-row-menu"
      buttonLabel={s.table.rowMenu(row.displayName)}
      buttonTitle={s.table.rowMenu(row.displayName)}
      buttonContent={<Ellipsis aria-hidden className="size-4" />}
      buttonProps={{ tabIndex: -1, 'data-row-menu': row.id }}
      menuLabel={s.table.rowMenu(row.displayName)}
      entries={callbacks.menu(row)}
    />
  );

  const onRowClick = (event: MouseEvent, row: VariableRow) => {
    if (event.shiftKey) {
      event.preventDefault();
      onSelect(row.id, 'range');
    }
  };

  if (layout === 'cards') {
    // Phones (m12): name and status, source and name in source, then the value and the row's actions.
    return (
      <ul
        ref={(element) => {
          body.current = element;
        }}
        role="list"
        aria-label={label}
        className="var-list var-cards"
        onKeyDown={onKeyDown}
      >
        {lines.map((line, index) =>
          line.kind === 'group' ? (
            <GroupHeader
              key={`g:${line.key}`}
              line={line}
              statuses={statuses}
              typeLabel={typeLabel}
              cell={cellProps(index, 0)}
              asListItem
              action={line.app ? groupAction?.(line.app) : null}
              menu={line.app ? groupMenu?.(line.app) : undefined}
              status={line.app ? (groupStatus?.(line.app) ?? null) : null}
              onToggle={() => onToggleGroup(line.key)}
            />
          ) : (
            <li
              key={line.row.id}
              role="listitem"
              aria-label={variableCellName(line.row) ?? line.row.displayName}
              data-row-id={line.row.id}
              data-testid={`variable-card-${line.row.id}`}
              className={`var-card ${selected.has(line.row.id) ? 'is-selected' : ''}`}
              onClick={(event) => onRowClick(event, line.row)}
              {...cellProps(index, 0)}
            >
              <div className="var-list-line">
                <VariableNameView row={line.row} />
                <span className="ml-auto shrink-0">
                  <StatusCellView statuses={statuses.get(line.row.id) ?? []} />
                </span>
              </div>
              <div className="var-list-line">
                <SourceChip location={line.row.location} kind={line.row.kind} />
                {line.row.location.name && (
                  <NativeName
                    name={line.row.location.name}
                    prefix={line.row.location.secret ? line.row.location.container : null}
                    className="min-w-0 text-[12px] text-fg-muted"
                  />
                )}
              </div>
              <div className="var-list-line var-card-value">
                <span className="min-w-0 flex-1">
                  {override?.(line.row, 'value') ?? (
                    <ValueCellView cell={cells.get(line.row.id)!} action={callbacks.valueAction(line.row)} />
                  )}
                </span>
                <span className="var-list-actions shrink-0">
                  {callbacks.primaryAction(line.row)}
                  {rowMenu(line.row)}
                </span>
              </div>
            </li>
          ),
        )}
      </ul>
    );
  }

  if (layout === 'list') {
    return (
      <ul
        ref={(element) => {
          body.current = element;
        }}
        role="list"
        aria-label={label}
        className="var-list"
        onKeyDown={onKeyDown}
      >
        {lines.map((line, index) =>
          line.kind === 'group' ? (
            <GroupHeader
              key={`g:${line.key}`}
              line={line}
              statuses={statuses}
              typeLabel={typeLabel}
              cell={cellProps(index, 0)}
              asListItem
              action={line.app ? groupAction?.(line.app) : null}
              menu={line.app ? groupMenu?.(line.app) : undefined}
              status={line.app ? (groupStatus?.(line.app) ?? null) : null}
              onToggle={() => onToggleGroup(line.key)}
            />
          ) : (
            <li
              key={line.row.id}
              role="listitem"
              aria-label={variableCellName(line.row) ?? line.row.displayName}
              data-row-id={line.row.id}
              className={`var-list-item ${selected.has(line.row.id) ? 'is-selected' : ''}`}
              onClick={(event) => onRowClick(event, line.row)}
              {...cellProps(index, 0)}
            >
              <div className="var-list-line">
                <VariableNameView row={line.row} />
                <span className="ml-auto flex shrink-0 items-center gap-1">
                  <SourceChip location={line.row.location} kind={line.row.kind} compact />
                </span>
              </div>
              <div className="var-list-line">
                <span className="min-w-0 flex-1 truncate">
                  {override?.(line.row, 'value') ?? (
                    <span className="flex min-w-0 items-center gap-1.5">
                      {line.row.location.name && (
                        <NativeName
                          name={line.row.location.name}
                          prefix={line.row.location.secret ? line.row.location.container : null}
                          className="min-w-0 text-[12px] text-fg-muted"
                          suffix=" →"
                        />
                      )}
                      <ValueCellView
                        cell={cells.get(line.row.id)!}
                        action={callbacks.valueAction(line.row)}
                      />
                    </span>
                  )}
                </span>
                <span className="shrink-0">
                  <StatusCellView statuses={statuses.get(line.row.id) ?? []} />
                </span>
              </div>
              <div className="var-list-actions">
                {callbacks.primaryAction(line.row)}
                {rowMenu(line.row)}
              </div>
            </li>
          ),
        )}
      </ul>
    );
  }

  const header = (column: ColumnId, col: number) => {
    const sorted = settings.sort?.column === column ? settings.sort.direction : undefined;
    const sortable = SORTABLE.has(column);
    return (
      <th
        key={column}
        role="columnheader"
        scope="col"
        aria-sort={sorted ?? undefined}
        className={`var-th var-col-${column}`}
      >
        {column === 'select' ? (
          <input
            type="checkbox"
            tabIndex={-1}
            aria-label={s.table.selectAll}
            checked={selected.size > 0 && lines.every((l) => l.kind === 'group' || selected.has(l.row.id))}
            onChange={() => (selected.size > 0 ? onClearSelection() : onSelectAll())}
          />
        ) : sortable ? (
          <button
            type="button"
            className="var-sort"
            onClick={() => onSort(column)}
            onKeyDown={(event) => {
              if (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                event.preventDefault();
                onMoveColumn(column, event.key === 'ArrowLeft' ? -1 : 1);
              }
            }}
            aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
          >
            {headerLabel(column)}
            {sorted === 'ascending' && <ArrowUp aria-hidden className="size-3" />}
            {sorted === 'descending' && <ArrowDown aria-hidden className="size-3" />}
          </button>
        ) : column === 'actions' ? (
          <span className="sr-only">{headerLabel(column)}</span>
        ) : (
          headerLabel(column)
        )}
        {col < 0 && null}
      </th>
    );
  };

  const cellContent = (row: VariableRow, column: ColumnId): ReactNode => {
    const replaced = override?.(row, column);
    if (replaced !== undefined) return replaced;
    switch (column) {
      case 'select':
        return (
          <input
            type="checkbox"
            tabIndex={-1}
            aria-label={s.table.selectRow(row.displayName)}
            checked={selected.has(row.id)}
            onChange={() => onSelect(row.id, 'toggle')}
            onClick={(event) => {
              if (event.shiftKey) {
                event.preventDefault();
                onSelect(row.id, 'range');
              }
            }}
          />
        );
      case 'variable':
        return <VariableNameView row={row} />;
      case 'source':
        return <SourceChip location={row.location} kind={row.kind} />;
      case 'nameInSource':
        return row.location.name ? (
          <NativeName name={row.location.name} prefix={row.location.secret ? row.location.container : null} />
        ) : (
          <span className="font-mono">{strings.common.dash}</span>
        );
      case 'value':
        return <ValueCellView cell={cells.get(row.id)!} action={callbacks.valueAction(row)} />;
      case 'status':
        return <StatusCellView statuses={statuses.get(row.id) ?? []} />;
      case 'resource':
        return <span className="font-mono">{row.resourceId}</span>;
      case 'lastChanged':
        return <span className="truncate text-[12px] text-fg-muted">{changedText(row)}</span>;
      case 'version':
        return (
          <span className="font-mono text-[12px]">
            {row.metadata?.currentVersion?.slice(0, 8) ?? (row.secret ? strings.common.dash : '')}
          </span>
        );
      case 'usedBy': {
        const others = [...new Set((row.metadata?.usedBy ?? []).map((u) => u.resourceId))].filter(
          (id) => id !== row.resourceId,
        );
        return <span className="truncate font-mono text-[12px]">{others.join(', ')}</span>;
      }
      case 'delivery':
        return <span className="truncate text-[12px]">{row.delivery?.label ?? ''}</span>;
      case 'rotation': {
        const rotation = row.location.rotation
          ? locationEntry(row.location).rotationText?.(row.location.rotation)
          : null;
        return rotation ? (
          <span
            className="truncate text-[12px]"
            title={rotation.help ?? undefined}
            data-testid={`rotation-${row.id}`}
          >
            {rotation.text}
          </span>
        ) : (
          <span className="text-fg-muted">{row.secret ? strings.vaults.rotationNone : ''}</span>
        );
      }
      case 'actions':
        return (
          <span className="flex items-center justify-end gap-1">
            {callbacks.primaryAction(row)}
            {rowMenu(row)}
          </span>
        );
    }
  };

  return (
    <table
      role={grouped ? 'treegrid' : 'grid'}
      aria-label={label}
      aria-multiselectable="true"
      className="table var-table"
    >
      <thead>
        <tr role="row">{columns.map((column, col) => header(column, col))}</tr>
      </thead>
      <tbody
        ref={(element) => {
          body.current = element;
        }}
        onKeyDown={onKeyDown}
      >
        {inserted?.after === '' && (
          <tr role="row" className="var-inserted">
            <td role="gridcell" colSpan={columns.length}>
              {inserted.node}
            </td>
          </tr>
        )}
        {lines.map((line, index) =>
          line.kind === 'group' ? (
            <GroupHeader
              key={`g:${line.key}`}
              line={line}
              statuses={statuses}
              typeLabel={typeLabel}
              colSpan={columns.length}
              cell={cellProps(index, 0)}
              action={line.app ? groupAction?.(line.app) : null}
              menu={line.app ? groupMenu?.(line.app) : undefined}
              status={line.app ? (groupStatus?.(line.app) ?? null) : null}
              onToggle={() => onToggleGroup(line.key)}
            />
          ) : (
            <RowLines
              key={line.row.id}
              row={line.row}
              level={grouped ? 2 : undefined}
              selected={selected.has(line.row.id)}
              unsaved={line.row.unsaved}
              removed={line.row.sync === 'removed'}
              insertedAfter={inserted?.after === line.row.id ? inserted.node : null}
              colSpan={columns.length}
              onClick={(event) => onRowClick(event, line.row)}
            >
              {columns.map((column, col) => (
                <td
                  key={column}
                  role="gridcell"
                  className={`var-td var-col-${column}`}
                  aria-label={column === 'variable' ? variableCellName(line.row) : undefined}
                  onDoubleClick={() => {
                    if (column === 'value' || column === 'variable')
                      callbacks.activate(line.row, column, 'F2');
                  }}
                  {...cellProps(index, col)}
                >
                  {cellContent(line.row, column)}
                </td>
              ))}
            </RowLines>
          ),
        )}
      </tbody>
    </table>
  );
}

function RowLines({
  row,
  level,
  selected,
  unsaved,
  removed,
  insertedAfter,
  colSpan,
  onClick,
  children,
}: {
  row: VariableRow;
  level?: number;
  selected: boolean;
  unsaved: boolean;
  removed: boolean;
  insertedAfter: ReactNode;
  colSpan: number;
  onClick: (event: MouseEvent) => void;
  children: ReactNode;
}) {
  return (
    <>
      <tr
        role="row"
        aria-level={level}
        aria-selected={selected}
        data-row-id={row.id}
        data-testid={`variable-${row.id}`}
        className={`var-row ${selected ? 'is-selected' : ''} ${unsaved ? 'is-unsaved' : ''} ${removed ? 'is-removed' : ''}`}
        onClick={onClick}
      >
        {children}
      </tr>
      {insertedAfter && (
        <tr role="row" className="var-inserted">
          <td role="gridcell" colSpan={colSpan}>
            {insertedAfter}
          </td>
        </tr>
      )}
    </>
  );
}

function GroupHeader({
  line,
  statuses,
  colSpan,
  cell,
  asListItem,
  typeLabel,
  action,
  menu,
  status,
  onToggle,
}: {
  line: Extract<Line, { kind: 'group' }>;
  action?: ReactNode;
  menu?: MenuEntry[];
  status?: string | null;
  statuses: ReadonlyMap<string, readonly RowStatus[]>;
  typeLabel?: (type: string) => string;
  colSpan?: number;
  cell: Record<string, unknown>;
  asListItem?: boolean;
  onToggle: () => void;
}) {
  const counts = issueCounts(line.rows.map((r) => statuses.get(r.id) ?? []));
  const Chevron = line.expanded ? ChevronDown : ChevronRight;
  const content = (
    <span className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        tabIndex={-1}
        className="btn btn-ghost btn-icon var-group-toggle"
        aria-label={line.expanded ? s.table.collapse(line.label) : s.table.expand(line.label)}
        onClick={onToggle}
      >
        <Chevron aria-hidden className="size-4" />
      </button>
      <span className="font-mono font-semibold">{line.label}</span>
      {line.app && <span className="text-fg-muted">{typeLabel?.(line.app.type) ?? line.app.type}</span>}
      <span className="text-fg-muted">· {s.table.variablesCount(line.rows.length)}</span>
      {counts.errors > 0 && <span className="badge tone-danger">{strings.dock.errors(counts.errors)}</span>}
      {counts.warnings > 0 && (
        <span className="badge tone-warning">{strings.dock.warnings(counts.warnings)}</span>
      )}
      {line.app?.referenceStatusError && (
        <StatusBadgeView
          status={{
            id: 'unknown',
            tone: 'warning',
            label: s.strips.referenceStatusError(line.app.referenceStatusError),
            help: '',
          }}
        />
      )}
      {(action || (menu && menu.length > 0)) && (
        <span className="ml-auto flex flex-none items-center gap-1">
          {action}
          {menu && menu.length > 0 && (
            <MenuButton
              buttonContent={<Ellipsis aria-hidden className="size-4" />}
              buttonClassName="btn btn-ghost btn-icon var-row-menu"
              buttonLabel={strings.vaults.groupRowMenu(line.label)}
              buttonTitle={strings.vaults.groupRowMenu(line.label)}
              buttonProps={{ tabIndex: -1, 'data-group-menu': line.key }}
              menuLabel={strings.vaults.groupRowMenu(line.label)}
              entries={menu}
              placement="below-end"
              portal
            />
          )}
        </span>
      )}
    </span>
  );
  const name = line.app
    ? strings.vaults.groupRowName(
        line.label,
        typeLabel?.(line.app.type) ?? line.app.type,
        s.table.variablesCount(line.rows.length),
        [
          counts.errors > 0 ? strings.dock.errors(counts.errors) : null,
          counts.warnings > 0 ? strings.dock.warnings(counts.warnings) : null,
        ]
          .filter(Boolean)
          .join(', '),
        status ?? null,
      )
    : undefined;
  if (asListItem)
    return (
      <li role="listitem" aria-expanded={line.expanded} aria-label={name} className="var-group" {...cell}>
        {content}
      </li>
    );
  return (
    <tr role="row" aria-level={1} aria-expanded={line.expanded} className="var-group">
      <td role="gridcell" colSpan={colSpan} aria-label={name} {...cell}>
        {content}
      </td>
    </tr>
  );
}
